// @ts-nocheck
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, X, ArrowRight, Plus, Check, ArrowRightLeft, Settings, Sparkles } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import ConfigCategoryPreview from '../ConfigCategoryPreview';
import { SessionExpiredError } from '../../utils/sessionExpiredError';
import './TBConverter.css';

const TBConverterUpload = ({ onAnalysisComplete, onProcessStart, onError, loading, userEmail }) => {
  const [files, setFiles] = useState([]); // Array of {file, sheets: [], selectedSheets: []}
  const [loadingSheets, setLoadingSheets] = useState({}); // file index -> loading state
  
  // Configuration selection state (matching FileUpload exactly)
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [useDefault, setUseDefault] = useState(false);

  // Fetch available configurations on mount (same endpoint as FileUpload)
  useEffect(() => {
    if (userEmail) {
      fetchConfigurations('income_statement');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);
  
  // Fetch configurations for the user's tenant (matching FileUpload)
  const fetchConfigurations = async (configType) => {
    setLoadingConfigs(true);
    try {
      await supabase.auth.getSession();
      
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/list/${encodeURIComponent(userEmail)}`,
        {
          params: { config_type: configType },
          withCredentials: true,
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      
      const configs = response.data.configs || [];
      setAvailableConfigs(configs);
      
      // Auto-select the first config if available
      if (configs.length > 0) {
        setSelectedConfig(configs[0]);
        setUseDefault(false);

      } else {
        setSelectedConfig(null);
      }
    } catch (error) {
      logger.error('❌ Error fetching configurations:', error);
      // Don't show error to user - just fall back to default
      setAvailableConfigs([]);
      setSelectedConfig(null);
    } finally {
      setLoadingConfigs(false);
    }
  };

  // Format category count text (matching FileUpload)
  const getCategoryCountText = (config) => {
    if (!config?.categories) return '0 categories';
    const count = config.categories.length;
    return `${count} ${count === 1 ? 'category' : 'categories'}`;
  };

  // Check if LLM guidelines are available (matching FileUpload)
  const hasLLMGuidelines = (config) => {
    return config?.llm_guidelines_available || (config?.llm_guidelines && config.llm_guidelines.length > 100);
  };

  // Fetch sheets for a file
  const fetchSheetsForFile = async (file, fileIndex) => {
    setLoadingSheets(prev => ({ ...prev, [fileIndex]: true }));
    const formData = new FormData();
    formData.append('file', file);

    try {
      await supabase.auth.getSession();
      
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/get-sheets/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      
      const sheets = response.data.sheets || [];
      
      setFiles(prev => {
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          sheets,
          selectedSheets: sheets.length > 0 ? [sheets[0]] : [] // Auto-select first sheet
        };
        return updated;
      });
      
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ Error fetching sheets:', error);
      onError(`Failed to read sheets from ${file.name}`);
    } finally {
      setLoadingSheets(prev => ({ ...prev, [fileIndex]: false }));
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    // Filter valid files first
    const validFiles = acceptedFiles.filter(file => {
      const name = file.name.toLowerCase();
      return name.endsWith('.xlsx') || name.endsWith('.xlsm') || name.endsWith('.csv');
    });
    const invalidFiles = acceptedFiles.filter(file => {
      const name = file.name.toLowerCase();
      return !name.endsWith('.xlsx') && !name.endsWith('.xlsm') && !name.endsWith('.csv');
    });

    // Report invalid files
    invalidFiles.forEach(file => {
      onError(`${file.name}: Please upload Excel files (.xlsx, .xlsm) or CSV files (.csv)`);
    });
    
    // Add valid files with correct indices
    if (validFiles.length > 0) {
      const startIndex = files.length;
      
      // Add all files first
      const newFiles = validFiles.map((file) => ({
        file,
        sheets: [],
        selectedSheets: []
      }));
      setFiles(prev => [...prev, ...newFiles]);
      
      // Then fetch sheets for each new file
      validFiles.forEach((file, idx) => {
        fetchSheetsForFile(file, startIndex + idx);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'text/csv': ['.csv']
    },
    disabled: loading
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleSheetSelection = (fileIndex, sheetName) => {
    setFiles(prev => {
      return prev.map((fileData, idx) => {
        if (idx !== fileIndex) return fileData;
        
        const isSelected = fileData.selectedSheets.includes(sheetName);
        return {
          ...fileData,
          selectedSheets: isSelected 
            ? fileData.selectedSheets.filter(s => s !== sheetName)
            : [...fileData.selectedSheets, sheetName]
        };
      });
    });
  };

  const getTotalSelectedSheets = () => {
    return files.reduce((sum, f) => sum + f.selectedSheets.length, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const totalSheets = getTotalSelectedSheets();
    if (totalSheets < 1) {
      onError('Please select at least one trial balance sheet');
      return;
    }

    // Build sheet selections array
    const sheetSelections = [];
    files.forEach((fileData, fileIndex) => {
      fileData.selectedSheets.forEach(sheetName => {
        sheetSelections.push({ file_index: fileIndex, sheet_name: sheetName });
      });
    });

    try {
      onProcessStart();
      
      await supabase.auth.getSession();
      
      const formData = new FormData();
      files.forEach((fileData) => {
        formData.append('files', fileData.file);
      });
      formData.append('sheet_selections', JSON.stringify(sheetSelections));

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tb-converter/analyze-structure/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          },
          timeout: 300000, // 5 minutes for structure analysis
        }
      );

      // Pass analysis result along with files, selections, and config
      onAnalysisComplete({
        analysisResult: response.data,
        files: files.map(f => f.file),
        sheetSelections,
        selectedConfig
      });
      
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('❌ TB structure analysis error:', error);
      onError(error.response?.data?.detail || error.message);
    }
  };

  const getSourceKey = (index) => String.fromCharCode(65 + index); // A, B, C, ...

  return (
    <div className="ccp-layout">
      <div className={`tb-converter-container border-beam-container ${loading ? 'loading' : ''}`}>
        <div className="border-beam"></div>

        <div className="upload-header">
          <div className="header-icon tb-icon">
            <ArrowRightLeft size={24} />
          </div>
          <div>
            <h3>Trial Balance to P&L Converter</h3>
            <p className="upload-description">Upload trial balance workbooks to convert them into P&L format for mapping</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="upload-form">
          {/* Compact Drop Zone */}
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''} ${files.length > 0 ? 'has-files' : ''} ${loading ? 'disabled' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="dropzone-content">
              <Upload size={24} className="upload-icon" />
              <p className="dropzone-text">
                {isDragActive ? "Drop files here..." : "Drag & drop Excel files, or click to browse"}
              </p>
              <span className="dropzone-hint">.xlsx, .xlsm, or .csv — one file per period, or multiple sheets per file</span>
            </div>
          </div>

          {/* Files List — compact rows */}
          {files.length > 0 && (
            <div className="files-list">
              <div className="files-header">
                <h4>Files & Sheets</h4>
                <span className="files-count">{getTotalSelectedSheets()} sheet{getTotalSelectedSheets() !== 1 ? 's' : ''} selected</span>
              </div>

              {files.map((fileData, fileIndex) => (
                <div key={fileIndex} className="file-row">
                  <span className="file-row-key">{getSourceKey(fileIndex)}</span>
                  <FileSpreadsheet size={16} className="file-row-icon" />
                  <div className="file-row-info">
                    <span className="file-row-name">{fileData.file.name}</span>
                    <span className="file-row-size">{(fileData.file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>

                  {/* Inline sheet pills */}
                  <div className="file-row-sheets">
                    {loadingSheets[fileIndex] ? (
                      <span className="sheet-pill-loading">
                        <span className="mini-spinner"></span>
                        Loading...
                      </span>
                    ) : fileData.sheets.length > 0 ? (
                      fileData.sheets.map(sheet => {
                        const isSelected = fileData.selectedSheets.includes(sheet);
                        return (
                          <button
                            key={sheet}
                            type="button"
                            className={`sheet-pill ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleSheetSelection(fileIndex, sheet)}
                            disabled={loading}
                          >
                            <span className="sheet-pill-check">
                              {isSelected && <Check size={8} strokeWidth={3} color="white" />}
                            </span>
                            {sheet}
                          </button>
                        );
                      })
                    ) : (
                      <span className="sheet-pill-loading">No sheets</span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFile(fileIndex)}
                    className="file-row-remove"
                    disabled={loading}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}

              {/* Add more — compact */}
              <div {...getRootProps()} className="add-more-row">
                <input {...getInputProps()} />
                <Plus size={14} />
                <span>Add more files</span>
              </div>
            </div>
          )}

          {/* Configuration Selector */}
          {files.length > 0 && (
            <div className="form-group config-selector-group">
              <label className="statement-type-label">
                <Settings size={12} className="label-icon" />
                Chart of Accounts Configuration:
              </label>

              {loadingConfigs ? (
                <div className="config-loading">
                  <div className="button-spinner"></div>
                  <span>Loading configurations...</span>
                </div>
              ) : availableConfigs.length > 0 ? (
                <div className="config-selector">
                  <div className="config-options">
                    {availableConfigs.map((config) => (
                      <div
                        key={config.id}
                        className={`config-option ${selectedConfig?.id === config.id ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!loading) {
                            setSelectedConfig(config);
                            setUseDefault(false);
                          }
                        }}
                      >
                        <div className="config-option-header">
                          <div className="radio-circle">
                            {selectedConfig?.id === config.id && <div className="radio-dot" />}
                          </div>
                          <div className="config-option-info">
                            <span className="config-name">
                              {config.config_name || config.tenant_name || 'My Configuration'}
                            </span>
                            <span className="config-meta">
                              {getCategoryCountText(config)}
                              {hasLLMGuidelines(config) && (
                                <span className="config-badge ai-badge">
                                  <Sparkles size={10} />
                                  AI Guidelines
                                </span>
                              )}
                            </span>
                          </div>
                          {selectedConfig?.id === config.id && (
                            <Check size={16} className="config-check" />
                          )}
                        </div>
                      </div>
                    ))}

                    <div
                      className={`config-option config-option-default ${!selectedConfig ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!loading) {
                          setSelectedConfig(null);
                          setUseDefault(true);
                        }
                      }}
                    >
                      <div className="config-option-header">
                        <div className="radio-circle">
                          {!selectedConfig && <div className="radio-dot" />}
                        </div>
                        <div className="config-option-info">
                          <span className="config-name">Use Default Configuration</span>
                          <span className="config-meta">Standard categories for P&L</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="config-empty-state">
                  <Settings size={18} className="config-empty-icon" />
                  <p>No saved configurations found.</p>
                  <span>Using default Income Statement categories.</span>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="analyze-button btn-animated"
            disabled={loading || files.length === 0 || getTotalSelectedSheets() < 1}
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                Analyzing structure...
              </>
            ) : (
              <>
                Analyze Trial Balance Structure
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      </div>

      {(selectedConfig || useDefault) && files.length > 0 && (
        <div className="ccp-layout-side">
          <ConfigCategoryPreview
            config={selectedConfig}
            isDefault={useDefault}
            configType="income_statement"
            mode="panel"
          />
        </div>
      )}
    </div>
  );
};

export default TBConverterUpload;
